import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { MenuLink } from '@/components/menu-link';
import { NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { InvoiceMoneyBar } from '@/components/sales/invoice-money-bar';
import { SalesTable } from '@/components/sales/sales-table';
import { useEInvoiceMap } from '@/lib/einvoicing';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { PERIODS, periodStart } from '@/lib/dates';
import { useOrgId } from '@/lib/org';
import { useSalesTransactions, type SalesRow } from '@/lib/sales';

export const Route = createFileRoute('/sales/all')({ component: AllSales });

const TYPES: Array<[string, string]> = [
  ['all', 'All transactions'],
  ['invoice', 'Invoices'],
  ['quote', 'Quotes'],
  ['credit_note', 'Credit notes'],
  ['sales_receipt', 'Sales receipts'],
  ['payment', 'Payments'],
];
const STATUSES: Array<[string, string, (r: SalesRow) => boolean]> = [
  ['all', 'All statuses', () => true],
  ['open', 'Open', (r) => ['approved', 'partially_paid', 'pending', 'accepted'].includes(r.status)],
  ['overdue', 'Overdue', (r) => r.overdue],
  ['draft', 'Draft', (r) => r.status === 'draft'],
  ['paid', 'Paid / closed', (r) => ['paid', 'posted', 'converted'].includes(r.status)],
  ['void', 'Void', (r) => r.status === 'void'],
];

/** All sales (document 06 S2): every sales document and payment with filters. */
function AllSales() {
  const orgId = useOrgId();
  const me = useMe();
  const tx = useSalesTransactions();
  const einvoices = useEInvoiceMap();
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [period, setPeriod] = useState('all');
  const [customer, setCustomer] = useState('all');
  const [q, setQ] = useState('');
  const [bar, setBar] = useState<string | null>(null);
  const customers = useMemo(
    () =>
      [...new Map((tx.data ?? []).map((r) => [r.contactId, r.contactName])).entries()].sort(
        (a, b) => a[1].localeCompare(b[1]),
      ),
    [tx.data],
  );
  const since = periodStart(period);
  const needle = q.trim().toLowerCase();
  const statusFn = STATUSES.find((s) => s[0] === status)![2];
  const rows = (tx.data ?? []).filter(
    (r) =>
      (type === 'all' || r.type === type) &&
      statusFn(r) &&
      (!since || r.date >= since) &&
      (customer === 'all' || r.contactId === customer) &&
      (!bar ||
        (r.type === 'invoice' &&
          ['approved', 'partially_paid'].includes(r.status) &&
          (bar === 'overdue' ? r.overdue : bar === 'not_due' ? !r.overdue : false))) &&
      (!needle ||
        r.contactName.toLowerCase().includes(needle) ||
        (r.number ?? '').toLowerCase().includes(needle) ||
        (r.memo ?? '').toLowerCase().includes(needle)),
  );
  return (
    <>
      <PageHeader
        title="All sales"
        actions={
          me.data?.isStaff ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-1">
                  New transaction <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <MenuLink to="/sales/invoices/new">Invoice</MenuLink>
                <MenuLink to="/sales/receive-payment/new">Payment</MenuLink>
                <MenuLink to="/sales/quotes/new">Quote</MenuLink>
                <MenuLink to="/sales/credit-notes/new">Credit note</MenuLink>
                <MenuLink to="/sales/sales-receipts/new">Sales receipt</MenuLink>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : (
        <>
          <InvoiceMoneyBar active={bar} onSelect={setBar} />
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <NativeSelect
              aria-label="Type"
              className="w-[180px] bg-card"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Status"
              className="w-[160px] bg-card"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Date"
              className="w-[160px] bg-card"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Customer"
              className="w-[220px] bg-card"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
            >
              <option value="all">All customers</option>
              {customers.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </NativeSelect>
            <div className="relative ml-auto w-[280px]">
              <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card pl-8"
                placeholder="Search no., customer or memo"
                aria-label="Search sales"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          {tx.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : tx.isError ? (
            <EmptyState title="Could not load sales" hint={tx.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={tx.data.length ? 'No transactions match these filters' : 'No sales yet'}
              action={
                me.data?.isStaff ? (
                  <Button asChild>
                    <Link to="/sales/invoices/new">New invoice</Link>
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <SalesTable rows={rows} einvoices={einvoices} batch />
          )}
        </>
      )}
    </>
  );
}
